import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { 
  Calculator, Users, FileText, Download, Loader2, CheckCircle2, 
  AlertCircle, User, Heart, Home, Briefcase, Shield, ChevronRight,
  ClipboardCheck, FileSpreadsheet
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const YearEndAdjustmentPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [bulkCalculating, setBulkCalculating] = useState(false);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  
  // 年選択
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  
  // 従業員一覧と選択
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // 年末調整データ
  const [adjustmentData, setAdjustmentData] = useState(null);
  
  // 計算結果
  const [calculationResult, setCalculationResult] = useState(null);
  
  // 確認ダイアログ
  const [confirmDialog, setConfirmDialog] = useState(false);
  
  // 一括処理ダイアログ
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetchEmployeeList();
  }, [selectedYear]);

  useEffect(() => {
    if (selectedEmployee) {
      fetchAdjustmentData();
      fetchCalculationResult();
    }
  }, [selectedEmployee, selectedYear]);

  const fetchEmployeeList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/year-end-adjustment/list/${selectedYear}`);
      setEmployees(res.data || []);
      if (res.data?.length > 0 && !selectedEmployee) {
        setSelectedEmployee(res.data[0].employee_id);
      }
    } catch (error) {
      console.error("Error fetching employee list:", error);
      toast.error("従業員一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdjustmentData = async () => {
    try {
      const res = await axios.get(`${API}/year-end-adjustment/${selectedEmployee}/${selectedYear}`);
      setAdjustmentData(res.data);
    } catch (error) {
      console.error("Error fetching adjustment data:", error);
    }
  };

  const fetchCalculationResult = async () => {
    try {
      const res = await axios.get(`${API}/year-end-adjustment/${selectedEmployee}/${selectedYear}/result`);
      setCalculationResult(res.data);
    } catch (error) {
      // 計算結果がない場合は正常
      setCalculationResult(null);
    }
  };

  const saveAdjustmentData = async () => {
    if (!adjustmentData) return;
    setSaving(true);
    try {
      await axios.post(`${API}/year-end-adjustment/${selectedEmployee}/${selectedYear}`, adjustmentData);
      toast.success("申告データを保存しました");
    } catch (error) {
      console.error("Error saving adjustment data:", error);
      toast.error("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const calculateAdjustment = async () => {
    setCalculating(true);
    try {
      // 先に保存
      await axios.post(`${API}/year-end-adjustment/${selectedEmployee}/${selectedYear}`, adjustmentData);
      // 計算実行
      const res = await axios.post(`${API}/year-end-adjustment/${selectedEmployee}/${selectedYear}/calculate`);
      setCalculationResult(res.data);
      toast.success("年末調整計算が完了しました");
      // 一覧を更新
      fetchEmployeeList();
    } catch (error) {
      console.error("Error calculating adjustment:", error);
      toast.error("計算に失敗しました");
    } finally {
      setCalculating(false);
    }
  };

  const confirmAdjustment = async () => {
    try {
      await axios.post(`${API}/year-end-adjustment/${selectedEmployee}/${selectedYear}/confirm`);
      toast.success("年末調整を確定しました");
      setConfirmDialog(false);
      fetchEmployeeList();
      fetchCalculationResult();
    } catch (error) {
      console.error("Error confirming adjustment:", error);
      toast.error("確定に失敗しました");
    }
  };

  const downloadWithholdingSlip = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/documents/withholding-slip/${selectedEmployee}/${selectedYear}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const emp = employees.find(e => e.employee_id === selectedEmployee);
      link.download = `源泉徴収票_${emp?.name || selectedEmployee}_${selectedYear}年.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("源泉徴収票をダウンロードしました");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("ダウンロードに失敗しました");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // 一括計算
  const bulkCalculate = async () => {
    setBulkCalculating(true);
    setBulkResult(null);
    try {
      const res = await axios.post(`${API}/year-end-adjustment/bulk-calculate/${selectedYear}`);
      setBulkResult(res.data);
      toast.success(`${res.data.calculated}名の年末調整計算が完了しました`);
      fetchEmployeeList();
      if (selectedEmployee) {
        fetchCalculationResult();
      }
    } catch (error) {
      console.error("Bulk calculate error:", error);
      toast.error("一括計算に失敗しました");
    } finally {
      setBulkCalculating(false);
    }
  };

  // 一括確定
  const bulkConfirm = async () => {
    setBulkConfirming(true);
    try {
      const res = await axios.post(`${API}/year-end-adjustment/bulk-confirm/${selectedYear}`);
      toast.success(res.data.message);
      setBulkDialog(false);
      setBulkResult(null);
      fetchEmployeeList();
    } catch (error) {
      console.error("Bulk confirm error:", error);
      toast.error("一括確定に失敗しました");
    } finally {
      setBulkConfirming(false);
    }
  };

  const updateField = (field, value) => {
    setAdjustmentData(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (amount) => `¥${(amount || 0).toLocaleString()}`;

  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return <Badge className="bg-emerald-600">確定済</Badge>;
      case "calculated":
        return <Badge className="bg-blue-600">計算済</Badge>;
      default:
        return <Badge className="bg-slate-600">未処理</Badge>;
    }
  };

  const selectedEmpInfo = employees.find(e => e.employee_id === selectedEmployee);

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calculator className="w-7 h-7 text-amber-500" />
            年末調整
          </h1>
          <p className="text-slate-400 text-sm mt-1">従業員の年末調整計算と源泉徴収票の発行</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setBulkDialog(true)}
            variant="outline"
            className="border-amber-600 text-amber-400 hover:bg-amber-600/20"
            data-testid="btn-bulk-menu"
          >
            <Users className="w-4 h-4 mr-2" />
            一括処理
          </Button>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28 bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...Array(3)].map((_, i) => {
                const year = currentYear - i;
                return <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 従業員一覧 */}
        <Card className="bg-slate-800 border-slate-700 lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Users className="w-4 h-4" />
              対象従業員
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto">
                {employees.map((emp) => (
                  <button
                    key={emp.employee_id}
                    onClick={() => setSelectedEmployee(emp.employee_id)}
                    className={`w-full px-4 py-3 text-left border-b border-slate-700 hover:bg-slate-700/50 transition-colors ${
                      selectedEmployee === emp.employee_id ? 'bg-slate-700' : ''
                    }`}
                    data-testid={`emp-${emp.employee_code}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium text-sm">{emp.name}</p>
                        <p className="text-slate-400 text-xs">{emp.employee_code} | {emp.department}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(emp.status)}
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* メインコンテンツ */}
        <div className="lg:col-span-3 space-y-4">
          {selectedEmployee && adjustmentData ? (
            <>
              {/* 従業員情報ヘッダー */}
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {selectedEmpInfo?.name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">{selectedEmpInfo?.name}</p>
                        <p className="text-slate-400 text-sm">
                          {selectedEmpInfo?.employee_code} | 年間給与: {formatCurrency(selectedEmpInfo?.total_salary)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(adjustmentData.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Tabs defaultValue="input" className="space-y-4">
                <TabsList className="bg-slate-800 border border-slate-700 flex flex-wrap h-auto p-1">
                  <TabsTrigger value="input" className="data-[state=active]:bg-amber-600 gap-2">
                    <ClipboardCheck className="w-4 h-4" />
                    申告入力
                  </TabsTrigger>
                  <TabsTrigger value="result" className="data-[state=active]:bg-amber-600 gap-2">
                    <FileSpreadsheet className="w-4 h-4" />
                    計算結果
                  </TabsTrigger>
                </TabsList>

                {/* 申告入力タブ */}
                <TabsContent value="input" className="space-y-4">
                  {/* 配偶者・扶養親族 */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Heart className="w-4 h-4 text-pink-400" />
                        配偶者・扶養親族
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300">控除対象配偶者あり</Label>
                        <Switch
                          checked={adjustmentData.has_spouse}
                          onCheckedChange={(v) => updateField("has_spouse", v)}
                        />
                      </div>
                      {adjustmentData.has_spouse && (
                        <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-slate-600">
                          <div>
                            <Label className="text-slate-400 text-xs">配偶者の年間所得</Label>
                            <Input
                              type="number"
                              value={adjustmentData.spouse_income}
                              onChange={(e) => updateField("spouse_income", parseInt(e.target.value) || 0)}
                              className="bg-slate-900 border-slate-700"
                            />
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-slate-400 text-xs">一般扶養親族（人）</Label>
                          <Input
                            type="number"
                            min="0"
                            value={adjustmentData.dependents_general}
                            onChange={(e) => updateField("dependents_general", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">特定扶養親族（19-22歳）</Label>
                          <Input
                            type="number"
                            min="0"
                            value={adjustmentData.dependents_specific}
                            onChange={(e) => updateField("dependents_specific", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">老人扶養親族（70歳以上）</Label>
                          <Input
                            type="number"
                            min="0"
                            value={adjustmentData.dependents_elderly}
                            onChange={(e) => updateField("dependents_elderly", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">同居老親等</Label>
                          <Input
                            type="number"
                            min="0"
                            value={adjustmentData.dependents_elderly_cohabit}
                            onChange={(e) => updateField("dependents_elderly_cohabit", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 保険料控除 */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-400" />
                        保険料控除
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-400 text-xs">新生命保険料</Label>
                          <Input
                            type="number"
                            value={adjustmentData.life_insurance_new}
                            onChange={(e) => updateField("life_insurance_new", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">旧生命保険料</Label>
                          <Input
                            type="number"
                            value={adjustmentData.life_insurance_old}
                            onChange={(e) => updateField("life_insurance_old", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">介護医療保険料</Label>
                          <Input
                            type="number"
                            value={adjustmentData.medical_insurance_new}
                            onChange={(e) => updateField("medical_insurance_new", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">新個人年金保険料</Label>
                          <Input
                            type="number"
                            value={adjustmentData.pension_insurance_new}
                            onChange={(e) => updateField("pension_insurance_new", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">旧個人年金保険料</Label>
                          <Input
                            type="number"
                            value={adjustmentData.pension_insurance_old}
                            onChange={(e) => updateField("pension_insurance_old", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">地震保険料</Label>
                          <Input
                            type="number"
                            value={adjustmentData.earthquake_insurance}
                            onChange={(e) => updateField("earthquake_insurance", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-400 text-xs">社会保険料（自己負担分）</Label>
                          <Input
                            type="number"
                            value={adjustmentData.social_insurance_paid}
                            onChange={(e) => updateField("social_insurance_paid", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                            placeholder="国民年金等の自己支払分"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">小規模企業共済等掛金</Label>
                          <Input
                            type="number"
                            value={adjustmentData.small_enterprise_mutual}
                            onChange={(e) => updateField("small_enterprise_mutual", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 住宅ローン控除 */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Home className="w-4 h-4 text-green-400" />
                        住宅借入金等特別控除
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-400 text-xs">住宅借入金等の年末残高</Label>
                          <Input
                            type="number"
                            value={adjustmentData.housing_loan_balance}
                            onChange={(e) => updateField("housing_loan_balance", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">住宅借入金等特別控除額</Label>
                          <Input
                            type="number"
                            value={adjustmentData.housing_loan_deduction}
                            onChange={(e) => updateField("housing_loan_deduction", parseInt(e.target.value) || 0)}
                            className="bg-slate-900 border-slate-700"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={adjustmentData.housing_loan_first_year}
                          onCheckedChange={(v) => updateField("housing_loan_first_year", v)}
                        />
                        <Label className="text-slate-300 text-sm">初年度（税務署への確定申告が必要）</Label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* その他控除 */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <User className="w-4 h-4 text-purple-400" />
                        その他の控除
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={adjustmentData.has_disability}
                            onCheckedChange={(v) => updateField("has_disability", v)}
                          />
                          <Label className="text-slate-300 text-sm">障害者</Label>
                        </div>
                        {adjustmentData.has_disability && (
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={adjustmentData.disability_special}
                              onCheckedChange={(v) => updateField("disability_special", v)}
                            />
                            <Label className="text-slate-300 text-sm">特別障害者</Label>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={adjustmentData.is_widow}
                            onCheckedChange={(v) => updateField("is_widow", v)}
                          />
                          <Label className="text-slate-300 text-sm">寡婦</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={adjustmentData.is_single_parent}
                            onCheckedChange={(v) => updateField("is_single_parent", v)}
                          />
                          <Label className="text-slate-300 text-sm">ひとり親</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={adjustmentData.is_working_student}
                            onCheckedChange={(v) => updateField("is_working_student", v)}
                          />
                          <Label className="text-slate-300 text-sm">勤労学生</Label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 前職情報 */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-orange-400" />
                        前職情報（中途入社の場合）
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={adjustmentData.has_previous_employer}
                          onCheckedChange={(v) => updateField("has_previous_employer", v)}
                        />
                        <Label className="text-slate-300 text-sm">前職あり</Label>
                      </div>
                      {adjustmentData.has_previous_employer && (
                        <div className="grid grid-cols-3 gap-4 pl-4 border-l-2 border-slate-600">
                          <div>
                            <Label className="text-slate-400 text-xs">前職の給与</Label>
                            <Input
                              type="number"
                              value={adjustmentData.previous_salary}
                              onChange={(e) => updateField("previous_salary", parseInt(e.target.value) || 0)}
                              className="bg-slate-900 border-slate-700"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-400 text-xs">前職の源泉徴収税額</Label>
                            <Input
                              type="number"
                              value={adjustmentData.previous_tax}
                              onChange={(e) => updateField("previous_tax", parseInt(e.target.value) || 0)}
                              className="bg-slate-900 border-slate-700"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-400 text-xs">前職の社会保険料</Label>
                            <Input
                              type="number"
                              value={adjustmentData.previous_social_insurance}
                              onChange={(e) => updateField("previous_social_insurance", parseInt(e.target.value) || 0)}
                              className="bg-slate-900 border-slate-700"
                            />
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* アクションボタン */}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={saveAdjustmentData}
                      disabled={saving}
                      variant="outline"
                      className="border-slate-600 hover:bg-slate-700"
                      data-testid="btn-save"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      保存
                    </Button>
                    <Button
                      onClick={calculateAdjustment}
                      disabled={calculating}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="btn-calculate"
                    >
                      {calculating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                      計算実行
                    </Button>
                  </div>
                </TabsContent>

                {/* 計算結果タブ */}
                <TabsContent value="result" className="space-y-4">
                  {calculationResult ? (
                    <>
                      {/* 収入・所得 */}
                      <Card className="bg-slate-800 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white text-base">収入・所得</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-slate-900 p-4 rounded-lg">
                              <p className="text-slate-400 text-xs">支払金額（年間給与）</p>
                              <p className="text-xl font-bold text-white">{formatCurrency(calculationResult.total_salary)}</p>
                            </div>
                            <div className="bg-slate-900 p-4 rounded-lg">
                              <p className="text-slate-400 text-xs">給与所得控除後の金額</p>
                              <p className="text-xl font-bold text-emerald-400">{formatCurrency(calculationResult.salary_income)}</p>
                            </div>
                            <div className="bg-slate-900 p-4 rounded-lg">
                              <p className="text-slate-400 text-xs">所得控除の額の合計額</p>
                              <p className="text-xl font-bold text-blue-400">{formatCurrency(calculationResult.total_deductions)}</p>
                            </div>
                            <div className="bg-slate-900 p-4 rounded-lg">
                              <p className="text-slate-400 text-xs">課税所得</p>
                              <p className="text-xl font-bold text-amber-400">{formatCurrency(calculationResult.taxable_income)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 所得控除内訳 */}
                      <Card className="bg-slate-800 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white text-base">所得控除内訳</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            {Object.entries(calculationResult.deductions || {}).map(([key, value]) => (
                              <div key={key} className="flex justify-between items-center py-2 px-3 bg-slate-900 rounded">
                                <span className="text-slate-400 text-sm">
                                  {key === "basic" && "基礎控除"}
                                  {key === "social_insurance" && "社会保険料"}
                                  {key === "life_insurance" && "生命保険料"}
                                  {key === "earthquake" && "地震保険料"}
                                  {key === "spouse" && "配偶者控除"}
                                  {key === "dependents" && "扶養控除"}
                                  {key === "disability" && "障害者控除"}
                                  {key === "widow" && "寡婦控除"}
                                  {key === "single_parent" && "ひとり親控除"}
                                  {key === "working_student" && "勤労学生控除"}
                                  {key === "small_enterprise" && "小規模共済"}
                                </span>
                                <span className="text-white font-medium">{formatCurrency(value)}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* 税額計算 */}
                      <Card className="bg-slate-800 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white text-base">税額計算</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-slate-900 p-4 rounded-lg">
                              <p className="text-slate-400 text-xs">算出所得税額</p>
                              <p className="text-xl font-bold text-white">{formatCurrency(calculationResult.income_tax)}</p>
                            </div>
                            <div className="bg-slate-900 p-4 rounded-lg">
                              <p className="text-slate-400 text-xs">住宅ローン控除</p>
                              <p className="text-xl font-bold text-green-400">-{formatCurrency(calculationResult.housing_deduction)}</p>
                            </div>
                            <div className="bg-slate-900 p-4 rounded-lg">
                              <p className="text-slate-400 text-xs">復興特別所得税</p>
                              <p className="text-xl font-bold text-white">{formatCurrency(calculationResult.reconstruction_tax)}</p>
                            </div>
                            <div className="bg-slate-900 p-4 rounded-lg">
                              <p className="text-slate-400 text-xs">年税額</p>
                              <p className="text-xl font-bold text-amber-400">{formatCurrency(calculationResult.annual_tax)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 過不足額 */}
                      <Card className={`border-2 ${calculationResult.tax_difference > 0 ? 'border-red-500 bg-red-950/30' : 'border-emerald-500 bg-emerald-950/30'}`}>
                        <CardContent className="p-6">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div>
                              <p className="text-slate-300 text-sm mb-1">過不足税額</p>
                              <p className={`text-3xl font-bold ${calculationResult.tax_difference > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                                {calculationResult.tax_difference > 0 ? '+' : ''}{formatCurrency(calculationResult.tax_difference)}
                              </p>
                              <p className="text-slate-400 text-sm mt-2">
                                源泉徴収済み: {formatCurrency(calculationResult.total_tax_withheld)} → 年税額: {formatCurrency(calculationResult.annual_tax)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {calculationResult.tax_difference > 0 ? (
                                <Badge className="bg-red-600 text-lg px-4 py-2">不足（徴収）</Badge>
                              ) : calculationResult.tax_difference < 0 ? (
                                <Badge className="bg-emerald-600 text-lg px-4 py-2">還付</Badge>
                              ) : (
                                <Badge className="bg-slate-600 text-lg px-4 py-2">過不足なし</Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* アクションボタン */}
                      <div className="flex flex-wrap gap-3">
                        {calculationResult.status !== "confirmed" && (
                          <Button
                            onClick={() => setConfirmDialog(true)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="btn-confirm"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            年末調整を確定
                          </Button>
                        )}
                        <Button
                          onClick={downloadWithholdingSlip}
                          disabled={downloadingPdf}
                          variant="outline"
                          className="border-slate-600 hover:bg-slate-700"
                          data-testid="btn-download-slip"
                        >
                          {downloadingPdf ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Download className="w-4 h-4 mr-2" />
                          )}
                          源泉徴収票PDF
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardContent className="p-12 text-center">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                        <p className="text-slate-400">計算結果がありません</p>
                        <p className="text-slate-500 text-sm mt-2">「申告入力」タブで情報を入力し、計算を実行してください</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </>
          ) : (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                <p className="text-slate-400">従業員を選択してください</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* 確定確認ダイアログ */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">年末調整の確定</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedEmpInfo?.name}さんの{selectedYear}年分の年末調整を確定します。確定後は修正できません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              キャンセル
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmAdjustment}>
              確定する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 一括処理ダイアログ */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-500" />
              一括処理（{selectedYear}年）
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              全従業員の年末調整を一括で計算・確定します
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* 一括計算 */}
            <div className="p-4 bg-slate-900 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium">一括計算</h4>
                  <p className="text-slate-400 text-sm">給与データがある全従業員の年末調整を計算</p>
                </div>
                <Button
                  onClick={bulkCalculate}
                  disabled={bulkCalculating}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="btn-bulk-calculate"
                >
                  {bulkCalculating ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Calculator className="w-4 h-4 mr-2" />
                  )}
                  一括計算
                </Button>
              </div>
            </div>

            {/* 計算結果 */}
            {bulkResult && (
              <div className="p-4 bg-slate-900 rounded-lg space-y-3">
                <h4 className="text-white font-medium">計算結果</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-2 bg-slate-800 rounded">
                    <p className="text-slate-400 text-xs">対象者</p>
                    <p className="text-white font-bold text-lg">{bulkResult.total}</p>
                  </div>
                  <div className="text-center p-2 bg-emerald-900/50 rounded">
                    <p className="text-emerald-400 text-xs">計算完了</p>
                    <p className="text-emerald-400 font-bold text-lg">{bulkResult.calculated}</p>
                  </div>
                  <div className="text-center p-2 bg-slate-700 rounded">
                    <p className="text-slate-400 text-xs">スキップ</p>
                    <p className="text-slate-300 font-bold text-lg">{bulkResult.skipped}</p>
                  </div>
                  <div className="text-center p-2 bg-red-900/50 rounded">
                    <p className="text-red-400 text-xs">エラー</p>
                    <p className="text-red-400 font-bold text-lg">{bulkResult.errors}</p>
                  </div>
                </div>
                
                {/* 詳細リスト */}
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-700">
                        <th className="text-left py-2">社員</th>
                        <th className="text-right py-2">年間給与</th>
                        <th className="text-right py-2">年税額</th>
                        <th className="text-right py-2">過不足</th>
                        <th className="text-center py-2">状態</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResult.details.map((d, idx) => (
                        <tr key={idx} className="border-b border-slate-700/50">
                          <td className="py-2 text-white">{d.employee_code} {d.name}</td>
                          <td className="py-2 text-right text-slate-300">
                            {d.total_salary ? formatCurrency(d.total_salary) : '-'}
                          </td>
                          <td className="py-2 text-right text-slate-300">
                            {d.annual_tax !== undefined ? formatCurrency(d.annual_tax) : '-'}
                          </td>
                          <td className={`py-2 text-right font-medium ${
                            d.tax_difference < 0 ? 'text-emerald-400' : d.tax_difference > 0 ? 'text-red-400' : 'text-slate-300'
                          }`}>
                            {d.tax_difference !== undefined ? (
                              d.tax_difference < 0 ? `還付 ${formatCurrency(Math.abs(d.tax_difference))}` :
                              d.tax_difference > 0 ? `徴収 ${formatCurrency(d.tax_difference)}` : '±0'
                            ) : '-'}
                          </td>
                          <td className="py-2 text-center">
                            {d.status === 'calculated' && <Badge className="bg-emerald-600">完了</Badge>}
                            {d.status === 'skipped' && <Badge className="bg-slate-600">スキップ</Badge>}
                            {d.status === 'error' && <Badge className="bg-red-600">エラー</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 一括確定 */}
            <div className="p-4 bg-slate-900 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium">一括確定</h4>
                  <p className="text-slate-400 text-sm">計算済みの全従業員の年末調整を確定</p>
                </div>
                <Button
                  onClick={bulkConfirm}
                  disabled={bulkConfirming || !bulkResult || bulkResult.calculated === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-bulk-confirm"
                >
                  {bulkConfirming ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  一括確定
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkDialog(false); setBulkResult(null); }}>
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YearEndAdjustmentPage;
